import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Grid, IconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { FieldArray, useFormikContext } from 'formik';
import { Add, Delete, DragIndicator } from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormLabelField } from '../../formComponents/FormLabelField';
import { FormTextField } from '../../formComponents/FormTextField';
import { generateObjectId, SettingsFormValues } from './WidgetSection';

const ADDON_ICON_SUGGESTIONS = ['🎂', '🎉', '👥', '👨‍👩‍👧', '💼', '🍕', '🍣', '🥐', '☕', '🍷', '🎨', '🪵'];

const SortableOption = ({ id, disabled, children }: { id: string; disabled: boolean; children: React.ReactNode }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  return (
    <Grid
      ref={setNodeRef}
      container
      flexDirection="row"
      flexWrap="nowrap"
      alignItems="flex-start"
      gap={1}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={{ p: 2, border: '1px solid', borderColor: 'mb.lineSoft', borderRadius: '6px' }}>
      <Grid {...listeners} {...attributes} style={{ cursor: disabled ? 'default' : 'grab', paddingTop: 4 }}>
        <DragIndicator fontSize="small" color="action" />
      </Grid>
      <Grid container flexDirection="column" gap={2} flex={1}>
        {children}
      </Grid>
    </Grid>
  );
};

export const AddonEditor = ({
  index,
  disabled,
  onDelete,
}: {
  index: number;
  disabled: boolean;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<SettingsFormValues>();
  const addon = values.addons[index];
  const basePath = `addons[${index}]`;

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );

  return (
    <Grid
      container
      flexDirection="column"
      gap={2}
      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>
          {t('editOrderPage.addons.addonLabel')}
        </Typography>
        <IconButton size="small" onClick={onDelete} disabled={disabled} color="default">
          <Delete fontSize="small" />
        </IconButton>
      </Grid>

      <FormLabelField
        labelFieldName={`${basePath}.label`}
        label={t('editOrderPage.addons.addonLabel')}
        disabled={disabled}
      />

      <FieldArray name={`${basePath}.options`}>
        {({ push: _push, remove, move }) => {
          const handleDragEnd = (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = addon.options.findIndex((o) => o.id === active.id);
            const newIndex = addon.options.findIndex((o) => o.id === over.id);
            if (oldIndex !== -1 && newIndex !== -1) {
              move(oldIndex, newIndex);
            }
          };

          return (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
              <SortableContext items={addon.options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                <Grid container flexDirection="column" gap={2}>
                  {addon.options.map((_, optIdx) => {
                    const optPath = `${basePath}.options[${optIdx}]`;
                    return (
                      <SortableOption key={addon.options[optIdx].id} id={addon.options[optIdx].id} disabled={disabled}>
                        <Grid container justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight={500}>
                            {t('editOrderPage.addons.optionLabel')} {optIdx + 1}
                          </Typography>
                          <IconButton size="small" onClick={() => remove(optIdx)} disabled={disabled} color="default">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Grid>
                        <FormLabelField
                          labelFieldName={`${optPath}.label`}
                          label={t('editOrderPage.addons.optionLabel')}
                          disabled={disabled}
                        />
                        <Grid container gap={2}>
                          <FormTextField
                            isFieldUpdate
                            fieldName={`${optPath}.base_price`}
                            label={t('editOrderPage.addons.basePrice')}
                            disabled={disabled}
                            type="number"
                            width={150}
                            min={0}
                          />
                          <FormTextField
                            isFieldUpdate
                            fieldName={`${optPath}.price_per_additional_value`}
                            label={t('editOrderPage.addons.pricePerPerson')}
                            disabled={disabled}
                            type="number"
                            width={150}
                            min={0}
                          />
                          <FormTextField
                            isFieldUpdate
                            fieldName={`${optPath}.icon`}
                            label={t('editOrderPage.addons.optionIcon')}
                            caption={t('editOrderPage.addons.optionIconCaption')}
                            placeholder="🎂"
                            disabled={disabled}
                            width={110}
                          />
                        </Grid>
                        <Grid container gap={0.5}>
                          {ADDON_ICON_SUGGESTIONS.map((emoji) => (
                            <Chip
                              key={emoji}
                              label={emoji}
                              size="small"
                              variant={addon.options[optIdx].icon === emoji ? 'filled' : 'outlined'}
                              disabled={disabled}
                              onClick={() =>
                                setFieldValue(`${optPath}.icon`, addon.options[optIdx].icon === emoji ? '' : emoji)
                              }
                            />
                          ))}
                        </Grid>
                      </SortableOption>
                    );
                  })}
                </Grid>
              </SortableContext>
            </DndContext>
          );
        }}
      </FieldArray>

      <FieldArray name={`${basePath}.options`}>
        {({ push }) => (
          <Button
            startIcon={<Add />}
            onClick={() =>
              push({
                id: generateObjectId(),
                label: {},
                base_price: 0,
                price_per_additional_value: 0,
                icon: '',
              })
            }
            disabled={disabled}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ alignSelf: 'flex-start' }}>
            {t('editOrderPage.addons.addOption')}
          </Button>
        )}
      </FieldArray>
    </Grid>
  );
};
